import type { BillingDraftListItem, BillingOverview, BillingUnbilledClient } from '@fos/shared/contracts/billing';
import type { MoneyTotal } from '@fos/shared/money';
import { ChevronRight, Clock, Info, Plus, ReceiptText, SearchX, ShieldCheck, Wallet } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { cn } from '../../../lib/cn';
import { formatMoneyTotal, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { ClientChip } from '../../../ui/EntityLabel';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { PageHeader, SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { Stat } from '../../../ui/Stat';
import { EmptyState, ErrorState, ListSkeleton, Skeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { TabPanel, Tabs } from '../../../ui/Tabs';
import { ClientDot } from '../../clients';
import { MobileFab, MobileFilters, TOUCH_BUTTON, useIsPhone } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import {
  clientMonthText,
  clockText,
  dayMonthText,
  displayStatus,
  hoursMinutesText,
  moneyShort,
  paidInMonthLabel,
  periodText,
  useBillingMode,
} from '../billing-format';
import { BillingStatsView } from '../components/BillingStats';
import { NewDraftDialog } from '../components/NewDraftDialog';

type StatusTab = 'all' | 'draft' | 'pending' | 'unpaid' | 'paid' | 'cancelled';

const STATUS_TABS: Array<{ value: StatusTab; label: string; settlementLabel: string; param: string }> = [
  { value: 'all', label: 'Wszystkie', settlementLabel: 'Wszystkie', param: '' },
  { value: 'draft', label: 'Robocze', settlementLabel: 'Szkice', param: 'robocze' },
  { value: 'pending', label: 'Do akceptacji', settlementLabel: 'Do akceptacji', param: 'akceptacja' },
  { value: 'unpaid', label: 'Do zapłaty', settlementLabel: 'Wysłane', param: 'do-zaplaty' },
  { value: 'paid', label: 'Opłacone', settlementLabel: 'Opłacone', param: 'oplacone' },
  { value: 'cancelled', label: 'Anulowane', settlementLabel: 'Anulowane', param: 'anulowane' },
];

function tabFromParam(value: string | null): StatusTab {
  return STATUS_TABS.find((t) => t.param !== '' && t.param === value)?.value ?? 'all';
}

function matchesTab(d: BillingDraftListItem, tab: StatusTab): boolean {
  switch (tab) {
    case 'all':
      return d.status !== 'cancelled';
    case 'draft':
      return d.status === 'draft' || d.status === 'error';
    case 'pending':
      return d.status === 'pending_approval';
    case 'unpaid':
      return d.status === 'issued' && !d.paidAt;
    case 'paid':
      return d.status === 'issued' && Boolean(d.paidAt);
    case 'cancelled':
      return d.status === 'cancelled';
  }
}

/**
 * Rozliczenia: lista (Figma 09, 3033:3673). Kafelki (niezafakturowane, czeka na akceptację, do zapłaty, opłacone
 * w miesiącu), sekcja „Do rozliczenia” (klienci z niezafakturowanym czasem i „Przygotuj zestawienie”), tabela
 * „Zestawienia i faktury” z filtrami statusu i klienta. Bez „Widzi stawki i kwoty” — same godziny (serwer nie zwraca
 * kwot). Nowe zestawienie: moduł Rozliczenia (Edycja) + „Przygotowuje zestawienia”.
 * Workspace bez systemu faktur (DECISIONS 2026-10-09): „Rozliczenia z klientem” — statusy Szkic / Wysłane do klienta
 * / Opłacone / Anulowane i filtr po nich, kolumna „Nr faktury” (opcjonalny numer z programu księgowego).
 */
export function BillingListPage() {
  const access = useAccess();
  if (!access.can('billing')) return <ModulePage module="billing" />;
  return <BillingList />;
}

interface Prepare {
  clientId: string | null;
  periodFrom?: string;
  periodTo?: string;
}

function BillingList() {
  const access = useAccess();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = tabFromParam(params.get('status'));
  const clientFilter = params.get('klient') ?? '';
  const seesRates = access.flag('seesRates');
  const canPrepare = access.can('billing', 'edit') && access.flag('preparesBilling');
  const { settlement } = useBillingMode();
  const [prepare, setPrepare] = useState<Prepare | null>(null);
  const phone = useIsPhone();
  // zakładka „Statystyki” (przychód) tylko z „Widzi stawki i kwoty” — serwer i tak sprawdza (billing.stats)
  const view = seesRates && params.get('widok') === 'statystyki' ? 'stats' : 'list';

  const drafts = trpc.billing.list.useQuery({ includeCancelled: true }, { enabled: view === 'list' });
  const overview = trpc.billing.overview.useQuery(undefined, { enabled: view === 'list' });

  const setParam = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const all = drafts.data ?? [];
  const clientOptions = useMemo(() => {
    const map = new Map<string, { value: string; label: string; leading: ReactNode }>();
    for (const d of all) {
      map.set(d.client.id, {
        value: d.client.id,
        label: d.client.name,
        leading: <ClientDot color={d.client.color} />,
      });
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'pl'));
  }, [all]);
  const items = all.filter((d) => matchesTab(d, tab) && (!clientFilter || d.client.id === clientFilter));
  const filtered = tab !== 'all' || Boolean(clientFilter);

  let table: ReactNode;
  if (drafts.isPending) table = <ListSkeleton rows={5} />;
  else if (drafts.isError) {
    table = <ErrorState onRetry={() => void drafts.refetch()} retrying={drafts.isFetching} />;
  } else if (items.length === 0) {
    table = filtered ? (
      <EmptyState
        icon={SearchX}
        title="Brak zestawień dla tych filtrów"
        action={
          <Button
            variant="secondary"
            onClick={() =>
              setParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('status');
                  next.delete('klient');
                  return next;
                },
                { replace: true },
              )
            }
          >
            Wyczyść filtry
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={ReceiptText}
        title="Nie ma jeszcze zestawień"
        description={
          settlement
            ? 'Zestawienie zbiera nierozliczony czas klienta. Wyślij je klientowi, a po wpłacie oznacz jako opłacone.'
            : 'Zestawienie zbiera niezafakturowany czas klienta. Fakturę wystawimy po Twojej akceptacji.'
        }
        action={
          canPrepare ? (
            <Button variant="secondary" icon={Plus} onClick={() => setPrepare({ clientId: null })}>
              Nowe zestawienie
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    table = (
      <DraftsTable
        items={items}
        showAmounts={seesRates}
        settlement={settlement}
        onOpen={(id) => navigate(`/rozliczenia/${id}`)}
      />
    );
  }

  const list = (
    <>
      <OverviewTiles query={overview} seesRates={seesRates} settlement={settlement} />
      {overview.data && overview.data.toBill.length > 0 && (
        <ToBillSection
          clients={overview.data.toBill}
          seesRates={seesRates}
          canPrepare={canPrepare}
          onPrepare={(c) => setPrepare({ clientId: c.client.id, periodFrom: c.firstDate, periodTo: c.lastDate })}
        />
      )}
      <Section className="mt-4 md:mt-6" aria-labelledby="billing-drafts-title">
        <div className="flex items-center gap-3">
          <h2 id="billing-drafts-title" className="min-w-0 flex-1 text-body-s font-semibold text-fg">
            {settlement ? 'Rozliczenia z klientem' : 'Zestawienia i faktury'}
          </h2>
          {/* telefon: status i klient w dolnym panelu „Filtry” (lifting 17.10.2026) */}
          {phone && (
            <MobileFilters
              count={(tab !== 'all' ? 1 : 0) + (clientFilter ? 1 : 0)}
              onClear={() =>
                setParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('status');
                    next.delete('klient');
                    return next;
                  },
                  { replace: true },
                )
              }
              showLabel="Pokaż zestawienia"
            >
              <Select
                aria-label={settlement ? 'Status rozliczenia' : 'Status zestawienia'}
                value={tab}
                onValueChange={(t) => setParam('status', STATUS_TABS.find((x) => x.value === t)?.param ?? '')}
                options={STATUS_TABS.map((t) => ({ value: t.value, label: settlement ? t.settlementLabel : t.label }))}
              />
              {clientOptions.length > 1 && (
                <Select
                  aria-label="Klient"
                  value={clientFilter || 'all'}
                  onValueChange={(v) => setParam('klient', v === 'all' ? '' : v)}
                  options={[{ value: 'all', label: 'Wszyscy klienci' }, ...clientOptions]}
                />
              )}
            </MobileFilters>
          )}
        </div>
        <div className={cn('mt-3 flex flex-col gap-3 md:flex-row md:items-center md:gap-2.5', phone && 'hidden')}>
          <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0">
            <Segmented
              aria-label={settlement ? 'Status rozliczenia' : 'Status zestawienia'}
              value={tab}
              onValueChange={(t) => setParam('status', STATUS_TABS.find((x) => x.value === t)?.param ?? '')}
              options={STATUS_TABS.map((t) => ({ value: t.value, label: settlement ? t.settlementLabel : t.label }))}
            />
          </div>
          <span className="hidden flex-1 md:block" />
          {clientOptions.length > 1 && (
            <Select
              aria-label="Klient"
              className="md:w-[220px]"
              value={clientFilter || 'all'}
              onValueChange={(v) => setParam('klient', v === 'all' ? '' : v)}
              options={[{ value: 'all', label: 'Wszyscy klienci' }, ...clientOptions]}
            />
          )}
        </div>
        <div className="mt-3">{table}</div>
        <p className="mt-4 flex items-start gap-2 text-caption text-fg-muted">
          <Info size={14} strokeWidth={1.75} aria-hidden className="mt-px shrink-0" />
          {settlement
            ? 'Rozliczenie: szkic → wysłane do klienta → opłacone. Do opłacenia możesz je zmieniać; numer faktury z programu księgowego dopiszesz w rozliczeniu.'
            : 'VAT i numer faktury nadaje system fakturowy. Wystawienie zawsze po Twojej akceptacji.'}
        </p>
      </Section>
    </>
  );

  return (
    <Page>
      <PageHeader
        title="Rozliczenia"
        actions={
          canPrepare && view === 'list' ? (
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setPrepare({ clientId: null })}
              className="max-md:hidden"
            >
              Nowe zestawienie
            </Button>
          ) : undefined
        }
      />
      {seesRates ? (
        <Tabs
          className="mt-6"
          aria-label="Widok Rozliczeń"
          value={view}
          onValueChange={(v) => setParam('widok', v === 'stats' ? 'statystyki' : '')}
          items={[
            { value: 'list', label: 'Zestawienia' },
            { value: 'stats', label: 'Statystyki' },
          ]}
        >
          <TabPanel value="list" className="pt-0">
            {list}
          </TabPanel>
          <TabPanel value="stats" className="pt-0">
            <BillingStatsView />
          </TabPanel>
        </Tabs>
      ) : (
        list
      )}
      {phone && canPrepare && view === 'list' && (
        <MobileFab icon={Plus} onClick={() => setPrepare({ clientId: null })}>
          Nowe zestawienie
        </MobileFab>
      )}
      <NewDraftDialog
        open={prepare !== null}
        onOpenChange={(open) => !open && setPrepare(null)}
        clientId={prepare?.clientId ?? null}
        periodFrom={prepare?.periodFrom}
        periodTo={prepare?.periodTo}
        onCreated={(d) => navigate(`/rozliczenia/${d.id}`)}
      />
    </Page>
  );
}

function OverviewTiles({
  query,
  seesRates,
  settlement,
}: {
  query: { data?: BillingOverview; isPending: boolean; isError: boolean; refetch: () => unknown; isFetching: boolean };
  seesRates: boolean;
  settlement: boolean;
}) {
  if (query.isPending) {
    return (
      <div role="status" aria-label="Wczytywanie" className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[118px] rounded-xl" />
        ))}
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Section className="mt-6">
        <ErrorState
          compact
          title="Nie udało się wczytać podsumowania"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </Section>
    );
  }
  const o = query.data;
  const clients = `${o.unbilled.clients} ${plural(o.unbilled.clients, ['klient', 'klientów', 'klientów'])}`;
  const invoices = (n: number) =>
    `${n} ${plural(n, settlement ? ['rozliczenie', 'rozliczenia', 'rozliczeń'] : ['faktura', 'faktury', 'faktur'])}`;
  // kwoty per waluta: „1 200 zł + 850 EUR” (waluty się nie sumuje)
  const amountOr = (total: Partial<MoneyTotal>, fallback: ReactNode) =>
    seesRates && total.amount !== undefined
      ? formatMoneyTotal({ ...total, amount: total.amount }, { short: true })
      : fallback;
  return (
    <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Stat
        label={settlement ? 'Nierozliczone' : 'Niezafakturowane'}
        icon={Clock}
        value={amountOr(o.unbilled, hoursMinutesText(o.unbilled.seconds))}
        note={
          o.unbilled.clients === 0
            ? 'wszystko rozliczone'
            : seesRates
              ? `${hoursMinutesText(o.unbilled.seconds)} · ${clients}`
              : clients
        }
        showDot={false}
      />
      <Stat
        label="Czeka na akceptację"
        icon={ShieldCheck}
        value={amountOr(o.pending, o.pending.count)}
        note={
          o.pending.first ? clientMonthText(o.pending.first.clientName, o.pending.first.periodFrom) : 'nic nie czeka'
        }
        tone="accent"
        showDot={Boolean(o.pending.first)}
        to={o.pending.count > 0 ? '/rozliczenia?status=akceptacja' : undefined}
      />
      <Stat
        label="Do zapłaty"
        icon={ReceiptText}
        value={amountOr(o.toPay, o.toPay.count)}
        note={
          o.toPay.count === 0
            ? 'wszystko opłacone'
            : o.toPay.overdue > 0
              ? `${invoices(o.toPay.count)} · ${o.toPay.overdue} po terminie`
              : `${invoices(o.toPay.count)}${o.toPay.nearestDueDate ? ` · termin ${dayMonthText(o.toPay.nearestDueDate)}` : ''}`
        }
        tone={o.toPay.overdue > 0 ? 'danger' : 'warning'}
        showDot={o.toPay.count > 0}
        to={o.toPay.count > 0 ? '/rozliczenia?status=do-zaplaty' : undefined}
      />
      <Stat
        label={paidInMonthLabel(o.paidThisMonth.month)}
        icon={Wallet}
        value={amountOr(o.paidThisMonth, o.paidThisMonth.count)}
        note={o.paidThisMonth.count === 0 ? 'jeszcze nic' : invoices(o.paidThisMonth.count)}
        tone="success"
        showDot={o.paidThisMonth.count > 0}
      />
    </div>
  );
}

/** „Do rozliczenia”: klienci z niezafakturowanym czasem (od największego), „Przygotuj zestawienie”. */
function ToBillSection({
  clients,
  seesRates,
  canPrepare,
  onPrepare,
}: {
  clients: BillingUnbilledClient[];
  seesRates: boolean;
  canPrepare: boolean;
  onPrepare: (c: BillingUnbilledClient) => void;
}) {
  return (
    <Section className="mt-4 md:mt-6" aria-labelledby="billing-to-bill-title">
      <SectionHeader title={<span id="billing-to-bill-title">Do rozliczenia</span>} tone="strong" className="pb-1" />
      <ul>
        {clients.map((c) => (
          <li key={c.client.id} className="flex min-h-[56px] items-center gap-3 border-b border-line py-2">
            <ClientDot color={c.client.color} />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-body-s font-semibold text-fg">{c.client.name}</span>
              <span className="truncate text-caption text-fg-muted">
                {hoursMinutesText(c.seconds)} · {c.entries} {plural(c.entries, ['wpis', 'wpisy', 'wpisów'])} · od{' '}
                {dayMonthText(c.firstDate)}
                {seesRates && c.withoutRate > 0 && <span className="text-warning"> · {c.withoutRate} bez stawki</span>}
              </span>
            </div>
            {seesRates && c.amount !== undefined && (
              <Mono className="shrink-0 text-body-s text-fg">
                {formatMoneyTotal({ ...c, amount: c.amount }, { short: true })}
              </Mono>
            )}
            {canPrepare && (
              <Button
                variant="secondary"
                size="s"
                className={TOUCH_BUTTON}
                onClick={() => onPrepare(c)}
                aria-label={`Przygotuj zestawienie: ${c.client.name}`}
              >
                <span className="hidden sm:inline">Przygotuj zestawienie</span>
                <span className="sm:hidden">Przygotuj</span>
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** Tabela „Zestawienia i faktury” (Figma 09): klient i okres, czas, kwota netto, faktura, status. */
function DraftsTable({
  items,
  showAmounts,
  settlement,
  onOpen,
}: {
  items: BillingDraftListItem[];
  showAmounts: boolean;
  settlement: boolean;
  onOpen: (id: string) => void;
}) {
  const th = 'h-9 px-1 text-caption font-medium text-fg-muted';
  const phone = useIsPhone();
  return (
    <table className="w-full table-fixed border-collapse text-left text-body-s">
      <thead>
        <tr className="border-b border-line">
          <th scope="col" className={th}>
            Klient i okres
          </th>
          <th scope="col" className={cn(th, 'hidden w-[72px] text-right md:table-cell')}>
            Czas
          </th>
          {showAmounts && (
            <th scope="col" className={cn(th, 'w-[104px] text-right md:w-[116px]')}>
              Kwota netto
            </th>
          )}
          {!showAmounts && (
            <th scope="col" className={cn(th, 'w-[72px] text-right md:hidden')}>
              Czas
            </th>
          )}
          <th scope="col" className={cn(th, 'hidden w-[148px] pl-4 lg:table-cell')}>
            {settlement ? 'Nr faktury' : 'Faktura'}
          </th>
          <th scope="col" className={cn(th, 'hidden w-[190px] md:table-cell')}>
            Status
          </th>
          <th scope="col" className="hidden w-8 md:table-cell">
            <span className="sr-only">Otwórz</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((d) => {
          const status = displayStatus(d, settlement);
          const number = settlement ? d.invoiceNumber : d.invoice?.number;
          return (
            <tr
              key={d.id}
              onClick={() => onOpen(d.id)}
              className="cursor-pointer border-b border-line transition-colors hover:bg-surface"
            >
              <td className="h-14 px-1 pr-3">
                {/* lifting 17.10.2026: klient jako chip w swojej barwie, okres obok (telefon: pod spodem) */}
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="flex min-w-0 flex-col items-start gap-1 md:flex-row md:items-center md:gap-2.5">
                    <Link
                      to={`/rozliczenia/${d.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex min-w-0 max-w-full rounded-xs focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      <ClientChip client={d.client} />
                    </Link>
                    <span className="truncate text-fg">{periodText(d)}</span>
                    {/* telefon: status jako pigułka pod okresem (kolumna Status jest tylko na desktopie) */}
                    {phone && (
                      <StatusPill tone={status.tone} size="s" className="mt-1 self-start">
                        {status.label}
                      </StatusPill>
                    )}
                  </div>
                </div>
              </td>
              <td className="hidden px-1 text-right md:table-cell">
                <Mono className="text-caption text-fg">{clockText(d.totalSeconds)}</Mono>
              </td>
              {showAmounts && (
                <td className="px-1 text-right">
                  <Mono className="text-body-s text-fg">
                    {d.totalNet !== undefined ? moneyShort(d.totalNet, d.currency) : '—'}
                  </Mono>
                </td>
              )}
              {!showAmounts && (
                <td className="px-1 text-right md:hidden">
                  <Mono className="text-caption text-fg">{clockText(d.totalSeconds)}</Mono>
                </td>
              )}
              <td className="hidden truncate px-1 pl-4 lg:table-cell">
                {number ? (
                  <Mono
                    className="text-caption text-fg"
                    title={!settlement && d.invoice?.simulated ? 'Symulacja' : undefined}
                  >
                    {number}
                  </Mono>
                ) : (
                  <span className="text-fg-muted">—</span>
                )}
              </td>
              <td className="hidden px-1 md:table-cell">
                <StatusPill tone={status.tone}>{status.label}</StatusPill>
              </td>
              <td className="hidden px-1 md:table-cell">
                <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
